// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The ML developer page's data: what there is to train on, and what the training tools have
 * been asked to do.
 *
 * Two hooks rather than one because the two halves move at completely different speeds. The
 * training set changes when somebody maps a course — hours, at best — so it loads once and on
 * demand. A queued job changes every few seconds, and the whole point of the page is watching
 * one, so that half polls.
 */

/** A floor `training/extract_dataset.py` selects by, and how far short the course falls. */
export type MlShortfall = { kind: string; has: number; required: number };

/** A stored whole-course picture a crop could be cut from. */
export type MlTrainingImage = {
  /** esri-worldimagery | lantmateriet-orto | … */
  provider: string;
  /** basemap (RGB) | nir (near-infrared) */
  kind: string;
  url: string;
  imageryFetchedAt: string | null;
  createdAt: string | null;
  zoom: number | null;
  widthPx: number | null;
  heightPx: number | null;
};

/** One course as a potential source of training crops. */
export type MlTrainingCourse = {
  courseId: string;
  name: string;
  source: string;
  osmUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  /** OSM-sourced features per kind — the labels a crop would be cut against. */
  labelCounts: Record<string, number>;
  totalLabels: number;
  /** Empty when the extractor would select this course. */
  shortfall: MlShortfall[];
  eligible: boolean;
  images: MlTrainingImage[];
};

export type MlThresholds = { minBunkers: number; minFairways: number };

export type MlTrainingData = { thresholds: MlThresholds; courses: MlTrainingCourse[] };

/** One training-tool run requested of a batch worker. */
export type MlJob = {
  jobId: string;
  /** extract | train | evaluate */
  kind: string;
  args: string[];
  status: string;
  requestedAt: string;
  finishedAt: string | null;
  workerId: string | null;
  exitCode: number | null;
  outputTail: string | null;
  error: string | null;
  durationSeconds: number | null;
};

export type MlJobsSnapshot = {
  /**
   * Whether a worker advertising the `ml` capability has reported in recently. False means a
   * queued job simply waits — worth saying out loud, because the training worker runs off-cloud
   * on somebody's desk and is usually off.
   */
  workerOnline: boolean;
  jobs: MlJob[];
};

export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'forbidden' }
  /** `httpStatus` is absent when the request never got an answer at all (network, CORS). */
  | { status: 'failed'; httpStatus: number | null }
  | ({ status: 'ready'; loadedAt: number } & T);

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const bool = (row: Row, ...keys: string[]): boolean => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'boolean') return value;
  }
  return false;
};

const counts = (row: Row, ...keys: string[]): Record<string, number> => {
  for (const key of keys) {
    const value = row[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(value as Row)) {
        if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
      }
      return out;
    }
  }
  return {};
};

const rows = (root: Row, ...keys: string[]): Row[] => {
  for (const key of keys) {
    const value = root[key];
    if (Array.isArray(value)) return value as Row[];
  }
  return [];
};

/** Maps the BFF's `/api/geodata/ml/training-data` body. Exported for direct unit testing. */
export const mapTrainingData = (body: unknown): MlTrainingData => {
  const root = (body ?? {}) as Row;
  const thresholdRow = (root.thresholds ?? root.Thresholds ?? {}) as Row;

  return {
    thresholds: {
      minBunkers: num(thresholdRow, 'minBunkers', 'MinBunkers'),
      minFairways: num(thresholdRow, 'minFairways', 'MinFairways'),
    },
    courses: rows(root, 'courses', 'Courses')
      .map((row): MlTrainingCourse | null => {
        const courseId = str(row, 'courseId', 'CourseId');
        if (!courseId) return null;

        const shortfall = rows(row, 'shortfall', 'Shortfall')
          .map((s): MlShortfall | null => {
            const kind = str(s, 'kind', 'Kind');
            return kind ? { kind, has: num(s, 'has', 'Has'), required: num(s, 'required', 'Required') } : null;
          })
          .filter((s): s is MlShortfall => s !== null);

        return {
          courseId,
          name: str(row, 'name', 'Name') ?? '',
          source: str(row, 'source', 'Source') ?? '',
          osmUrl: str(row, 'osmUrl', 'OsmUrl'),
          latitude: numOrNull(row, 'latitude', 'Latitude'),
          longitude: numOrNull(row, 'longitude', 'Longitude'),
          labelCounts: counts(row, 'labelCounts', 'LabelCounts'),
          totalLabels: num(row, 'totalLabels', 'TotalLabels'),
          shortfall,
          // Trust the server's verdict when it sends one; fall back to the shortfall being
          // empty, which is the same rule, so an older service body still reads correctly.
          eligible: typeof row.eligible === 'boolean' || typeof row.Eligible === 'boolean'
            ? bool(row, 'eligible', 'Eligible')
            : shortfall.length === 0,
          images: rows(row, 'images', 'Images')
            .map((i): MlTrainingImage | null => {
              const url = str(i, 'url', 'Url');
              if (!url) return null;
              return {
                provider: str(i, 'provider', 'Provider') ?? '',
                kind: str(i, 'kind', 'Kind') ?? '',
                url,
                imageryFetchedAt: str(i, 'imageryFetchedAt', 'ImageryFetchedAt'),
                createdAt: str(i, 'createdAt', 'CreatedAt'),
                zoom: numOrNull(i, 'zoom', 'Zoom'),
                widthPx: numOrNull(i, 'widthPx', 'WidthPx'),
                heightPx: numOrNull(i, 'heightPx', 'HeightPx'),
              };
            })
            .filter((i): i is MlTrainingImage => i !== null),
        };
      })
      .filter((c): c is MlTrainingCourse => c !== null),
  };
};

/** Maps the BFF's `/api/geodata/ml/jobs` body. Exported for direct unit testing. */
export const mapJobs = (body: unknown): MlJobsSnapshot => {
  const root = (body ?? {}) as Row;
  return {
    workerOnline: bool(root, 'workerOnline', 'WorkerOnline'),
    jobs: rows(root, 'jobs', 'Jobs')
      .map((row): MlJob | null => {
        const jobId = str(row, 'jobId', 'JobId');
        if (!jobId) return null;
        const args = row.args ?? row.Args;
        return {
          jobId,
          kind: str(row, 'kind', 'Kind') ?? '',
          args: Array.isArray(args) ? args.filter((a): a is string => typeof a === 'string') : [],
          status: str(row, 'status', 'Status') ?? '',
          requestedAt: str(row, 'requestedAt', 'RequestedAt') ?? '',
          finishedAt: str(row, 'finishedAt', 'FinishedAt'),
          workerId: str(row, 'workerId', 'WorkerId'),
          exitCode: numOrNull(row, 'exitCode', 'ExitCode'),
          outputTail: str(row, 'outputTail', 'OutputTail'),
          error: str(row, 'error', 'Error'),
          durationSeconds: numOrNull(row, 'durationSeconds', 'DurationSeconds'),
        };
      })
      .filter((j): j is MlJob => j !== null),
  };
};

/** Loads the training-set view once, and again whenever `refresh` is called. */
export function useMlTrainingData(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean
): { state: LoadState<MlTrainingData>; refresh: () => void } {
  const [state, setState] = useState<LoadState<MlTrainingData>>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/training-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed', httpStatus: response.status });
          return;
        }
        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapTrainingData(body) });
      } catch {
        if (!cancelled) setState({ status: 'failed', httpStatus: null });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, enabled, reloadKey]);

  return { state, refresh };
}

/** The result of asking for a run: the page shows the server's own reason when it refuses. */
export type QueueResult =
  | { ok: true; jobId: string | null }
  | { ok: false; message: string | null; httpStatus: number | null };

/**
 * Polls the job history and queues new runs.
 *
 * A queue call refreshes immediately rather than waiting for the next tick, so the row the
 * developer just created appears under the button they just pressed.
 */
export function useMlJobs(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean,
  pollMs = 10000
): { state: LoadState<MlJobsSnapshot>; refresh: () => void; queue: (kind: string, args: string[]) => Promise<QueueResult> } {
  const [state, setState] = useState<LoadState<MlJobsSnapshot>>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed', httpStatus: response.status });
          return;
        }
        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapJobs(body) });
      } catch {
        if (!cancelled) setState({ status: 'failed', httpStatus: null });
      } finally {
        if (!cancelled) timer = setTimeout(load, pollMs);
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [apiBaseUrl, token, enabled, pollMs, reloadKey]);

  const queue = useCallback(
    async (kind: string, args: string[]): Promise<QueueResult> => {
      if (!token) return { ok: false, message: null, httpStatus: null };
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/jobs`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind, args }),
        });

        const text = await response.text();
        if (!response.ok) {
          // geodata refuses an unknown kind or a caller-supplied --dsn with a reason; showing
          // it beats "failed", because the reason is usually the fix.
          let message: string | null = null;
          try {
            const problem = JSON.parse(text) as Row;
            message = str(problem, 'detail', 'Detail', 'title', 'Title');
          } catch {
            message = text.length > 0 ? text : null;
          }
          return { ok: false, message, httpStatus: response.status };
        }

        refresh();
        try {
          const created = JSON.parse(text) as Row;
          return { ok: true, jobId: str(created, 'jobId', 'JobId') };
        } catch {
          return { ok: true, jobId: null };
        }
      } catch {
        return { ok: false, message: null, httpStatus: null };
      }
    },
    [apiBaseUrl, token, refresh]
  );

  return { state, refresh, queue };
}
